import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useCMS } from '../../context/CMSContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiPhone,
  FiMail,
  FiMapPin,
  FiLinkedin,
  FiTwitter,
  FiFacebook,
  FiArrowRight,
  FiSend,
  FiShield,
} = FiIcons;

const Footer = () => {
  const { content } = useCMS();
  const { company, services } = content;
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const currentYear = new Date().getFullYear();

  const handleSubscribe = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Encode form data properly for Netlify
    const data = new URLSearchParams();
    data.append('form-name', 'newsletter-form');
    data.append('email', email);

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: data.toString(),
    })
      .then(() => {
        setSubmitted(true);
        setEmail('');
        setIsSubmitting(false);

        setTimeout(() => {
          setSubmitted(false);
        }, 3000);
      })
      .catch(() => {
        setSubmitted(true);
        setEmail('');
        setIsSubmitting(false);

        setTimeout(() => {
          setSubmitted(false);
        }, 3000);
      });
  };

  const footerLinks = {
    services: services.items.slice(0, 5),
    company: [
      { name: 'Accueil', href: '/' },
      { name: 'Services', href: '/services' },
      { name: 'À propos', href: '/about' },
      { name: 'Contact', href: '/contact' },
    ],
    legal: [
      { name: 'Mentions Légales', href: '/legal?section=legal' },
      { name: 'Politique de Confidentialité', href: '/legal?section=privacy' },
      { name: "Conditions d'utilisation", href: '/legal?section=terms' },
    ],
  };

  return (
    <footer className="bg-slate-950 text-slate-300 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-gold-500 via-yellow-400 to-gold-500"></div>
      <div className="absolute -top-24 -right-24 w-64 h-64 bg-gold-600/10 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-gold-500/10 rounded-full blur-3xl"></div>

      {/* Newsletter Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 border-b border-slate-800">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl font-bold text-white mb-2">
              Restez informé de l'actualité QHSE
            </h3>
            <p className="text-slate-400">
              Inscrivez-vous pour recevoir nos veilles réglementaires et conseils d'experts.
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            onSubmit={handleSubscribe}
            name="newsletter-form"
            method="POST"
            className="flex flex-col sm:flex-row gap-3"
          >
            {submitted ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex-1 bg-green-500/20 border border-green-500 text-green-300 px-4 py-3 rounded-xl text-sm font-medium flex items-center"
              >
                ✓ Inscription confirmée !
              </motion.div>
            ) : (
              <>
                <input
                  type="email"
                  name="email"
                  placeholder="Votre adresse email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-gold-500 transition-all"
                />
                {/* Newsletter Button - Golden Yellow */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-gradient-to-r from-gold-500 to-yellow-400 hover:from-gold-600 hover:to-yellow-500 disabled:opacity-50 text-gray-900 px-6 py-3 rounded-xl font-semibold flex items-center justify-center transition-all group"
                >
                  {isSubmitting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900" />
                  ) : (
                    <>
                      S'abonner
                      <SafeIcon icon={FiSend} className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
              </>
            )}
          </motion.form>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="space-y-6">
            <Link to="/" className="inline-block">
              {content.company.logo ? (
                <img
                  src={content.company.logo}
                  alt={content.company.name}
                  className="h-20 md:h-24 w-auto object-contain rounded-lg"
                  style={{ transform: 'scale(0.9)' }}
                />
              ) : (
                <span className="text-2xl font-bold bg-gradient-to-r from-gold-400 to-gold-500 bg-clip-text text-transparent">
                  {company.name}
                </span>
              )}
            </Link>
            <p className="text-slate-400 leading-relaxed">{company.mission}</p>
          </div>

          {/* Services Column */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6 flex items-center">
              <span className="w-8 h-px bg-gold-500 mr-3"></span>
              Expertises
            </h4>
            <ul className="space-y-4">
              {footerLinks.services.map((service) => (
                <li key={service.id}>
                  <Link
                    to={`/services/${service.id}`}
                    className="text-slate-400 hover:text-gold-500 flex items-center group transition-colors"
                  >
                    <SafeIcon
                      icon={FiArrowRight}
                      className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 -ml-5 group-hover:ml-0 transition-all"
                    />
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links Column */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6 flex items-center">
              <span className="w-8 h-px bg-gold-500 mr-3"></span>
              Navigation
            </h4>
            <ul className="space-y-4">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-slate-400 hover:text-gold-500 flex items-center group transition-colors"
                  >
                    <SafeIcon
                      icon={FiArrowRight}
                      className="w-3 h-3 mr-2 opacity-0 group-hover:opacity-100 -ml-5 group-hover:ml-0 transition-all"
                    />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h4 className="text-white font-bold text-lg mb-6 flex items-center">
              <span className="w-8 h-px bg-gold-500 mr-3"></span>
              Contact
            </h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3 group">
                <div className="w-10 h-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-gold-500 group-hover:bg-gold-500 group-hover:text-white transition-all">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">Bureau</p>
                  <p className="text-sm text-slate-400">{company.address}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 group">
                <div className="w-10 h-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-gold-500 group-hover:bg-gold-500 group-hover:text-white transition-all">
                  <SafeIcon icon={FiPhone} className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">Téléphone</p>
                  <p className="text-sm text-slate-400">{company.phone}</p>
                </div>
              </div>

              <div className="flex items-start space-x-3 group">
                <div className="w-10 h-10 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center text-gold-500 group-hover:bg-gold-500 group-hover:text-white transition-all">
                  <SafeIcon icon={FiMail} className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-white">Email</p>
                  <p className="text-sm text-slate-400 truncate">{company.email}</p>
                </div>
              </div>
            </div>

            {/* Social Icons - Moved here */}
            <div className="flex space-x-4 mt-6">
              {[
                { icon: FiLinkedin, href: '#', color: 'hover:bg-blue-600' },
                { icon: FiTwitter, href: '#', color: 'hover:bg-sky-500' },
                { icon: FiFacebook, href: '#', color: 'hover:bg-blue-700' },
              ].map((social, i) => (
                <motion.a
                  key={i}
                  href={social.href}
                  whileHover={{ y: -3 }}
                  className={`w-10 h-10 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-white transition-all ${social.color}`}
                >
                  <SafeIcon icon={social.icon} className="w-5 h-5" />
                </motion.a>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-slate-950/50 border-t border-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              &copy;{currentYear}{' '}
              <span className="text-white font-medium">{company.name} SARL</span>. Tous droits réservés.
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              {footerLinks.legal.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  className="text-sm text-slate-500 hover:text-white transition-colors"
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <div className="flex items-center text-xs text-slate-600">
              <SafeIcon icon={FiShield} className="w-3 h-3 mr-1" />
              Certifié conforme aux normes CEMAC
            </div>
          </div>

          {/* Designer Credit */}
          <div className="mt-8 pt-8 border-t border-slate-800 text-center w-full">
            <p className="text-sm text-slate-400">
              Designed with <span className="text-red-500 text-lg">❤</span> by{' '}
              <a
                href="https://netvisible.net"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 hover:text-gold-500 transition-colors font-medium"
              >
                netvisible.net
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;